import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'src/store';
import type { BulkBriefsState, BulkBriefRow, BulkBriefStatus } from './types';
import {
  getClients,
  getDraft,
  saveDraft,
  deleteDraft,
  getMonthlyOverview,
  createBriefsFromDraft,
} from './thunks';

const initialState: BulkBriefsState = {
  clients: [],
  currentDraft: null,
  currentClientId: null,
  currentMonth: null,
  overview: null,
  loading: false,
  saving: false,
  creating: false,
  error: null,
  creationProgress: {
    total: 0,
    completed: 0,
    failed: 0,
    current: null,
  },
};

export const bulkBriefsSlice = createSlice({
  name: 'bulkBriefs',
  initialState,
  reducers: {
    // Set current client and month
    setCurrentDraft: (
      state,
      action: PayloadAction<{ clientId: string; month: string }>
    ) => {
      state.currentClientId = action.payload.clientId;
      state.currentMonth = action.payload.month;
    },

    // Add a new row to current draft
    addRow: (state, action: PayloadAction<BulkBriefRow>) => {
      if (state.currentDraft) {
        state.currentDraft.rows.push(action.payload);
        state.currentDraft.metadata.totalKeywords += 1;
        state.currentDraft.metadata.briefsPending += 1;
        state.currentDraft.metadata.articlesPending += 1;
      }
    },

    // Update a row
    updateRow: (
      state,
      action: PayloadAction<{ rowId: string; updates: Partial<BulkBriefRow> }>
    ) => {
      if (state.currentDraft) {
        const rowIndex = state.currentDraft.rows.findIndex(
          (r) => r.id === action.payload.rowId
        );
        if (rowIndex !== -1) {
          state.currentDraft.rows[rowIndex] = {
            ...state.currentDraft.rows[rowIndex],
            ...action.payload.updates,
          };
        }
      }
    },

    // Remove a row
    removeRow: (state, action: PayloadAction<string>) => {
      if (state.currentDraft) {
        const rowIndex = state.currentDraft.rows.findIndex(
          (r) => r.id === action.payload
        );
        if (rowIndex !== -1) {
          const row = state.currentDraft.rows[rowIndex];
          state.currentDraft.rows.splice(rowIndex, 1);
          state.currentDraft.metadata.totalKeywords -= 1;

          // Update counts based on row brief status
          if (row.briefStatus === 'created') {
            state.currentDraft.metadata.briefsCreated -= 1;
          } else if (row.briefStatus === 'pending') {
            state.currentDraft.metadata.briefsPending -= 1;
          } else if (row.briefStatus === 'error') {
            state.currentDraft.metadata.briefsError -= 1;
          }

          // Update counts based on row article status
          if (row.articleStatus === 'created') {
            state.currentDraft.metadata.articlesCreated -= 1;
          } else if (row.articleStatus === 'pending') {
            state.currentDraft.metadata.articlesPending -= 1;
          } else if (row.articleStatus === 'error') {
            state.currentDraft.metadata.articlesError -= 1;
          }

          // Update gdocs exported count
          if (row.gdocsLink) {
            state.currentDraft.metadata.gdocsExported -= 1;
          }
        }
      }
    },

    // Update row status (used during brief creation)
    updateRowStatus: (
      state,
      action: PayloadAction<{
        rowId: string;
        status: BulkBriefStatus;
        briefId?: string;
        briefLink?: string;
        errorMessage?: string;
        createdAt?: string;
        updatedAt?: string;
        stepRemains?: number;
      }>
    ) => {
      console.log('[AppSync-Reducer] updateRowStatus:', action.payload.rowId, 'briefId:', action.payload.briefId);

      if (state.currentDraft) {
        const rowIndex = state.currentDraft.rows.findIndex(
          (r) => r.id === action.payload.rowId
        );

        if (rowIndex !== -1) {
          const row = state.currentDraft.rows[rowIndex];
          const oldStatus = row.briefStatus;

          // Update row - brief fields
          row.briefStatus = action.payload.status;
          if (action.payload.briefId) {
            row.briefId = action.payload.briefId;
            row.briefLink = action.payload.briefLink || `/dashboard/briefs/${action.payload.briefId}`;
            row.briefDate = new Date().toLocaleDateString('en-GB'); // DD/MM/YY format
            console.log('[AppSync-Reducer] ✅ briefId set:', row.briefId, 'for row:', row.keyword);
          }
          if (action.payload.errorMessage) {
            row.briefErrorMessage = action.payload.errorMessage;
          }
          if (action.payload.createdAt) {
            row.createdAt = action.payload.createdAt;
          }
          if (action.payload.stepRemains !== undefined) {
            row.stepRemains = action.payload.stepRemains;
          }
          row.updatedAt = action.payload.updatedAt || new Date().toISOString();

          // Update metadata counts
          if (oldStatus === 'pending') state.currentDraft.metadata.briefsPending -= 1;
          if (oldStatus === 'created') state.currentDraft.metadata.briefsCreated -= 1;
          if (oldStatus === 'error') state.currentDraft.metadata.briefsError -= 1;
          // Note: 'creating' status doesn't affect metadata counts - it's a transient state

          if (action.payload.status === 'pending') {
            state.currentDraft.metadata.briefsPending += 1;
          } else if (action.payload.status === 'created') {
            state.currentDraft.metadata.briefsCreated += 1;
          } else if (action.payload.status === 'error') {
            state.currentDraft.metadata.briefsError += 1;
          }
          // 'creating' status: no metadata count change
        }
      }
    },

    // Update creation progress
    updateCreationProgress: (
      state,
      action: PayloadAction<{
        total: number;
        completed: number;
        current: string | null;
      }>
    ) => {
      state.creationProgress = {
        ...state.creationProgress,
        total: action.payload.total,
        completed: action.payload.completed,
        current: action.payload.current,
      };
    },

    // Reset creation progress
    resetCreationProgress: (state) => {
      state.creationProgress = {
        total: 0,
        completed: 0,
        failed: 0,
        current: null,
      };
    },

    // Clear current draft
    clearCurrentDraft: (state) => {
      state.currentDraft = null;
      state.currentClientId = null;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Get clients
    builder
      .addCase(getClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getClients.fulfilled, (state, action) => {
        state.loading = false;
        state.clients = action.payload;
      })
      .addCase(getClients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get draft
    builder
      .addCase(getDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDraft = action.payload;
        state.currentClientId = action.payload.clientId;
      })
      .addCase(getDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Save draft
    builder
      .addCase(saveDraft.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.saving = false;
        if (state.currentDraft) {
          state.currentDraft.metadata = action.payload.metadata;
        }
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    // Delete draft
    builder
      .addCase(deleteDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDraft.fulfilled, (state) => {
        state.loading = false;
        state.currentDraft = null;
        state.currentClientId = null;
      })
      .addCase(deleteDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get monthly overview
    builder
      .addCase(getMonthlyOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMonthlyOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload;
      })
      .addCase(getMonthlyOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create briefs from draft
    builder
      .addCase(createBriefsFromDraft.pending, (state) => {
        state.creating = true;
        state.error = null;
        state.creationProgress.failed = 0;
      })
      .addCase(createBriefsFromDraft.fulfilled, (state, action) => {
        state.creating = false;
        const failed = action.payload.filter((r) => !r.success).length;
        state.creationProgress.failed = failed;
        state.creationProgress.completed = action.payload.length;
      })
      .addCase(createBriefsFromDraft.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentDraft,
  addRow,
  updateRow,
  removeRow,
  updateRowStatus,
  updateCreationProgress,
  resetCreationProgress,
  clearCurrentDraft,
  clearError,
} = bulkBriefsSlice.actions;

// Selectors
export const selectBulkBriefs = (state: RootState) => state.bulkBriefs;
export const selectClients = (state: RootState) => state.bulkBriefs.clients;
export const selectCurrentDraft = (state: RootState) => state.bulkBriefs.currentDraft;
export const selectOverview = (state: RootState) => state.bulkBriefs.overview;
export const selectCreationProgress = (state: RootState) =>
  state.bulkBriefs.creationProgress;
export const selectIsLoading = (state: RootState) => state.bulkBriefs.loading;
export const selectIsSaving = (state: RootState) => state.bulkBriefs.saving;
export const selectIsCreating = (state: RootState) => state.bulkBriefs.creating;

export default bulkBriefsSlice.reducer;
